/** @jsx h */

import { h, Component, createRef } from 'preact'
import { debounce } from 'lodash-es'
import { getCountryByCurrency, getPaymentsEnabled } from '../collection/utils'

export default class InstallmentPayments extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showAfterPay: false,
      showPayPal: false,
      showKlarna: false,
      showShopPay: false,
      viewPort: window.innerWidth,
    }

    this.carouselRef = createRef()

    this.debouncedRefreshComponent = debounce(this.refreshComponent.bind(this), 300)
    this.debouncedRefreshCarousel = debounce(this.refreshCarousel.bind(this), 500)

    this.country = (window.countryCode ?? 'US').toUpperCase()
    this.settings = window.installmentPaymentSettings || {}
  }

  async componentDidMount() {
    const { showAfterPay, showKlarna, showShopPay, showPayPal } = getPaymentsEnabled(this.props.currency)

    if (showPayPal) {
      await this.addPayPalSdk()
    }

    this.setState({
      showAfterPay,
      showPayPal,
      showKlarna,
      showShopPay
    })

    window.addEventListener('resize', debounce(() => {
      if (window.innerWidth === this.state.viewPort) return
      this.setState({ viewPort: window.innerWidth })
    }, 300))
  }

  componentWillUnmount() {
    this.debouncedRefreshComponent.cancel()
    this.debouncedRefreshCarousel.cancel()
    this.destroyCarousel()
  }

  componentWillUpdate() {
    this.debouncedRefreshComponent.cancel()
    this.debouncedRefreshCarousel.cancel()
    this.destroyCarousel()
  }

  componentDidUpdate() {
    this.debouncedRefreshComponent()
  }

  hasCarousel() {
    return this.state.showAfterPay + this.state.showPayPal + this.state.showKlarna > 1
  }

  async refreshComponent() {
    await Promise.all([
      this.refreshAfterPay(),
      this.refreshPayPal(),
      this.refreshKlarna()
    ])

    this.debouncedRefreshCarousel()
  }

  async refreshCarousel() {
    if (this.carouselRef.current) {
      let carouselHeight = 0

      this.carouselRef.current.querySelectorAll('.installment-payments__item > *').forEach(item => {
        const { height: itemHeight } = item.getBoundingClientRect()

        if (carouselHeight < itemHeight) {
          carouselHeight = item.offsetHeight
        }
      })

      this.carouselRef.current.style.height = carouselHeight + 'px'
      this.carouselRef.current.querySelectorAll('.installment-payments__item').forEach(item => {
        item.style.height = carouselHeight + 'px'
      })

      if (this.hasCarousel()) {
        this.initCarousel()
      }
    }
  }

  initCarousel() {
    if (this.carouselRef.current) {
      const items = this.carouselRef.current.querySelectorAll('.installment-payments__item')
      const totalItems = items.length
      let activeIndex = 0

      const update = () => {
        items.forEach((item, index) => {
          if (index === activeIndex) {
            item.classList.add('is-active')
            item.classList.remove('is-previously-active')
          } else {
            const isPreviouslyActive = item.classList.contains('is-active')

            item.classList.remove('is-active')

            if (isPreviouslyActive) {
              item.classList.add('is-previously-active')
            } else {
              item.classList.remove('is-previously-active')
            }
          }
        })
      }

      const rotate = () => {
        activeIndex = (activeIndex + 1) % totalItems

        update()
        this.carouselRef.current.classList.add('is-carousel-enabled')
      }

      update()
      if (this.interval) {
        clearInterval(this.interval)
      }
      this.interval = setInterval(rotate, 5000)

      this.carouselRef.current.addEventListener('mouseenter', () => {
        if (this.isTouchEnabled()) {
          return
        }

        if (this.interval) {
          clearInterval(this.interval)
          this.interval = null
        }
      })

      this.carouselRef.current.addEventListener('mouseleave', () => {
        if (this.isTouchEnabled()) {
          return
        }

        if (this.interval) {
          clearInterval(this.interval)
          this.interval = null
        }
        this.interval = setInterval(rotate, 3000)
      })
    }
  }

  destroyCarousel() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }

    if (this.carouselRef.current) {
      this.carouselRef.current.removeAttribute('style')
      this.carouselRef.current.querySelectorAll('.installment-payments__item').forEach(item => item.removeAttribute('style'))
      this.carouselRef.current.classList.remove('is-carousel-enabled')
    }
  }

  injectCustomCSS(els, customCSS) {
    els.forEach(el => {
      if (el?.shadowRoot) {
        const styleEl = document.createElement('style')
        styleEl.innerHTML = customCSS

        const lastChildEl = [...el.shadowRoot.children].at(-1)

        el.shadowRoot.appendChild(styleEl)

        if (lastChildEl.tagName.toLowerCase() === 'style') {
          el.shadowRoot.removeChild(lastChildEl)
        }
      }
    })
  }

  isTouchEnabled() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0
  }

  async refreshAfterPay() {
    return new Promise(async resolve => {
      if (!this.state.showAfterPay || !this.carouselRef.current) {
        resolve()
        return
      }

      await this.waitForElements('afterpay-placement', ':shadow')

      const afterPayEls = this.carouselRef.current.querySelectorAll('afterpay-placement')
      const customCSS = this.props.afterPayCustomCSS ?? `
        .afterpay-paragraph {
          font-size: 12px !important;
          line-height: 12px;
          font-family: Verdana, Arial, Helvetica, sans-serif;
          font-weight: 400;
          color: #000;
          margin-top: 0;
          font-smoothing: auto;
          -webkit-font-smoothing: auto;
        }

        @media only screen and (max-width: 767px) {
          .afterpay-paragraph {
            font-size: 11px !important;
          }
        }

        @media only screen and (min-width: 768px) {
          .afterpay-paragraph {
            font-size: 12px !important;
            line-height: 16px;
          }
        }

        .afterpay-logo {
          padding-top: 0;
          width: auto !important;
          margin: 0;
          height: 20px;
          min-height: 12px !important;
          cursor: pointer;
          position: relative;
        }

        .payment-amount + span {
          padding-right: 0;
        }

        .afterpay-logo::after {
          content: '.';
          display: inline;
          vertical-align: 6px;
        }

        .afterpay-logo svg {
          --logo-badge-width: 64px;
          line-height: 12px;
        }

        @media only screen and (min-width: 768px) {
          .afterpay-logo svg {
            line-height: 16px;
          }
        }

        .afterpay-logo-link {
          position: relative;
        }

        #logo-link {
          font-family: inherit;
          font-size: inherit;
        }

        .afterpay-link slot {
          display: none;
        }

        .afterpay-link template::after {
          content: 'Learn more';
          text-decoration: underline;
        }

        .c-afterpay .afterpay-logo.brand-afterpay svg {
          max-width: 80px;
        }
      `

      this.injectCustomCSS(afterPayEls, customCSS)
      afterPayEls.forEach(el => el.removeAttribute('style'))
      resolve()
    })
  }

  async refreshPayPal() {
    return new Promise(async resolve => {
      if (!this.state.showPayPal || !this.carouselRef.current) {
        resolve()
        return
      }

      const els = this.carouselRef.current.querySelectorAll('.paypal-placement')

      els.forEach(el => {
        el.innerHTML = ''
        el.removeAttribute('data-pp-id')

        window.paypal.Messages({
          placement: 'product',
          buyerCountry: this.props.currency !== 'EUR' ? getCountryByCurrency(this.props.currency) : this.country,
          amount: Number(this.props.amount),
          currency: this.props.currency,
          style: {
            layout: 'text',
            logo: {
              type: 'inline'
            },
            text: {
              color: 'monochrome',
              align: this.props.align ?? 'left',
              fontFamily: 'Verdana',
              size: window.innerWidth < 768 ? 11 : 12
            }
          },
          onRender: async () => {
            await this.waitForElements('.paypal-placement', 'iframe[data-height]')
            const iframeEl = el.querySelector('iframe[data-height]')
            if (iframeEl) iframeEl.style.opacity = '1'
            resolve()
          }
        }).render(el)
      })
      resolve()
    })
  }

  async refreshKlarna() {
    return new Promise(async resolve => {
      if (!this.state.showKlarna || !this.carouselRef.current) {
        resolve()
        return
      }

      window.Klarna?.OnsiteMessaging?.refresh()

      await this.waitForElements('klarna-placement', 'div:shadow')

      const klarnaEls = this.carouselRef.current.querySelectorAll('klarna-placement > div')
      const customCSS = this.props.klarnaCustomCSS ?? `
        .container,
        .link {
          border: 0;
          text-align: ${this.props.align ?? 'left'};
          font-family: Verdana, Arial, Helvetica, sans-serif;
          font-size: 11px;
          line-height: 12px;
          font-weight: 400;
          color: #000;
          padding: 0;
          font-smoothing: auto;
          -webkit-font-smoothing: auto;
        }

        .logo {
          color: #000;
          font-size: 12px;
        }

        .link {
          text-transform: lowercase;
        }

        .link::first-letter {
          text-transform: uppercase;
        }

        @media (min-width: 768px) {
          .container,
          .link {
            font-size: 12px;
            line-height: 16px;
          }

          .logo {
            font-size: 14px;
          }
        }
      `

      this.injectCustomCSS(klarnaEls, customCSS)

      klarnaEls.forEach(el => {
        if (el?.shadowRoot) {
          el.shadowRoot.children[0].style.visibility = 'inherit'
        }
      })

      resolve()
    })
  }

  async waitForElements(parentSelector, childSelector, pollingTime = 300, maxTries = 10) {
    return new Promise(resolve => {
      if (!this.carouselRef.current) {
        resolve(false)
        return
      }

      let tries = 0
      let interval = setInterval(() => {
        tries++
        let isRendered = false

        if (this.carouselRef.current) {
          const els = [...this.carouselRef.current.querySelectorAll(parentSelector)]
          isRendered = els.every(el => {
            if (childSelector.includes(':shadow')) {
              const selector = childSelector.replaceAll(':shadow', '').trim()

              if (selector) {
                return el.querySelector(selector)?.shadowRoot
              }

              return el.shadowRoot
            }

            return el.querySelector(childSelector)
          })
        }

        if (tries >= maxTries || isRendered) {
          clearInterval(interval)
          resolve(isRendered)
        }
      }, pollingTime)
    })
  }

  async addPayPalSdk() {
    return new Promise(resolve => {
      if (window.paypal) {
        resolve()
      } else {
        const script = document.createElement('script')
        script.src = 'https://www.paypal.com/sdk/js?client-id=AQ1AFTPlzkwghvA9bta_K3hSCFIW_XC3GJ127Aq2wID5Q5wQMaiZL56577PKwFlnDsBlZtpvq9Twn6hz&components=messages,buttons'
        document.body.prepend(script)

        //
        let interval

        interval = setInterval(() => {
          if (window.paypal) {
            clearInterval(interval)
            resolve()
          }
        }, 500)
      }
    })
  }

  render() {
    const { showPayments } = getPaymentsEnabled(this.props.currency)

    if (!showPayments) {
      return ''
    }

    return (
      <div class={`installment-payments ${this.hasCarousel() ? 'has-carousel' : ''} ${this.state.showPayPal ? 'has-paypal' : ''}`}>
        <div ref={this.carouselRef} class="installment-payments__carousel">
          {this.state.showAfterPay && (
            <div class="installment-payments__item installment-payments__item--afterpay">
              <afterpay-placement
                data-locale={this.country === 'GB' ? 'en_GB' : 'en_US'}
                data-currency={this.props.currency}
                data-logo-type="lockup"
                data-amount={Number(this.props.amount).toFixed(2)}
                style="visibility:hidden"
              ></afterpay-placement>
            </div>
          )}

          {this.state.showPayPal && (
            <div class="installment-payments__item installment-payments__item--paypal">
              <div key={`paypal-placement-${this.state.viewPort}`} class="paypal-placement"></div>
            </div>
          )}

          {this.state.showKlarna && (
            <div class="installment-payments__item installment-payments__item--klarna">
              <klarna-placement
                v-pre
                data-key="credit-promotion-auto-size"
                data-locale="en-US"
                data-purchase-amount={Number(this.props.amount) * 100}
              ></klarna-placement>
            </div>
          )}

          {this.state.showShopPay && (
            <div class="installment-payments__item installment-payments__item-shoppay">
              <div class="installment-payment-shoppay">
                <svg class="installment-payment-shoppay__logo" xmlns="http://www.w3.org/2000/svg" fill="none" width="50" height="12" viewBox="0 0 4317 1037">
                  <path fill="#000" d="M1105.96 326.186c-34.62-72.633-100.26-119.551-199.174-119.551a198.287 198.287 0 0 0-87.198 21.908 198.272 198.272 0 0 0-68.555 58.168l-3.61 4.398V6.443A6.2 6.2 0 0 0 741.219.24H601.367a6.201 6.201 0 0 0-6.09 6.203V823c0 1.615.643 3.163 1.785 4.305a6.085 6.085 0 0 0 4.305 1.785h149.778a6.203 6.203 0 0 0 6.203-6.09V474.835c0-67.67 45.113-115.604 117.295-115.604 78.949 0 98.912 64.964 98.912 131.167v332.601a6.087 6.087 0 0 0 6.09 6.09h149.435c1.63 0 3.19-.637 4.35-1.778a6.207 6.207 0 0 0 1.86-4.312v-352.45c0-12.068 0-23.911-1.58-35.414a311.635 311.635 0 0 0-27.75-108.949ZM358.093 447.206s-76.242-17.933-104.325-25.151c-28.084-7.218-77.145-22.557-77.145-59.663 0-37.106 39.587-48.949 79.739-48.949 40.151 0 84.814 9.7 88.31 54.25a6.433 6.433 0 0 0 2.07 4.186 6.436 6.436 0 0 0 4.358 1.679l147.634-.564a6.322 6.322 0 0 0 4.633-2.007 6.315 6.315 0 0 0 1.683-4.761C495.915 223.668 370.837 172.69 255.459 172.69c-136.807 0-236.846 90.227-236.846 189.702 0 72.633 20.527 140.755 181.92 188.124 28.309 8.233 66.769 18.947 100.379 28.309 40.376 11.279 62.143 28.309 62.143 55.151 0 31.128-45.113 52.782-89.437 52.782-64.174 0-109.739-23.797-113.461-66.543a6.431 6.431 0 0 0-6.429-5.639l-147.295.677a6.422 6.422 0 0 0-4.628 1.963 6.417 6.417 0 0 0-1.801 4.692c6.767 134.551 136.694 207.07 257.825 207.07 180.454 0 261.996-101.504 261.996-196.581.226-44.663-10.038-146.394-161.732-185.191ZM2257.37 206.409c-74.99 0-137.82 41.504-178.31 91.582v-85.717c0-1.596-.63-3.129-1.75-4.267a6.033 6.033 0 0 0-4.23-1.822h-140.07c-1.61 0-3.17.64-4.31 1.782a6.11 6.11 0 0 0-1.79 4.307v800.766c.04 1.59.7 3.12 1.84 4.23a6.084 6.084 0 0 0 4.26 1.75h149.89a5.973 5.973 0 0 0 5.97-5.98V749.802h2.26c23.79 36.315 88.87 79.851 173.92 79.851 159.91 0 293.23-132.634 293.23-311.847.12-171.995-132.52-311.397-300.91-311.397Zm-13.87 470.648a158.925 158.925 0 0 1-89.3-24.819 159.024 159.024 0 0 1-60.27-70.418 158.879 158.879 0 0 1-10.72-92.063 158.847 158.847 0 0 1 42.45-82.388 158.929 158.929 0 0 1 81.21-44.69 158.92 158.92 0 0 1 164.35 66.536 159.047 159.047 0 0 1 27.25 88.591 157.093 157.093 0 0 1-11.05 60.339 156.606 156.606 0 0 1-33.35 51.483 156.533 156.533 0 0 1-50.56 34.743 156.708 156.708 0 0 1-60.01 12.686ZM1483.34 171.229c-139.74 0-209.44 47.482-265.39 85.491l-1.69 1.127a13.856 13.856 0 0 0-5.68 8.374 13.83 13.83 0 0 0 1.51 10.01l55.27 95.188a13.945 13.945 0 0 0 9.81 6.768 13.657 13.657 0 0 0 11.28-3.046l4.39-3.608c28.76-24.136 74.89-56.393 186.55-65.19 62.14-4.961 115.83 11.279 155.42 48.272 43.53 40.152 69.59 105.002 69.59 173.462 0 125.98-74.22 205.153-193.43 206.733-98.24-.564-164.22-51.768-164.22-127.446 0-40.152 18.16-66.317 53.58-92.483a13.785 13.785 0 0 0 5.38-8.015c.77-3.234.34-6.637-1.21-9.579l-49.62-93.837a14.619 14.619 0 0 0-3.65-4.361c-1.49-1.2-3.2-2.094-5.04-2.631a14 14 0 0 0-5.79-.466c-1.95.253-3.82.911-5.49 1.933-55.71 33.045-124.06 93.497-120.34 209.665 4.51 147.859 127.45 260.755 287.26 265.38h18.95c189.92-6.204 327.07-147.183 327.07-338.351 0-175.493-127.9-363.39-364.51-363.39ZM3148.93 261.455h-95.2a8.58 8.58 0 0 0-5.94 2.509 8.438 8.438 0 0 0-2.5 5.95v199.739a8.337 8.337 0 0 0 2.51 5.908 8.435 8.435 0 0 0 5.93 2.439h95.2c57.97 0 100.72-45.678 100.72-108.273s-42.75-108.272-100.72-108.272ZM3441.38 614.802c0 28.985 24.47 45.113 67.1 45.113 57.97 0 92.25-31.354 92.25-86.957v-16.014l-86.83 4.511c-45.8 2.255-72.52 21.317-72.52 53.347Z"/>
                  <path fill="#000" d="M4146.27 2.05H2867.3a159.64 159.64 0 0 0-61.13 12.167 159.492 159.492 0 0 0-51.82 34.648 159.599 159.599 0 0 0-34.62 51.85 159.397 159.397 0 0 0-12.12 61.15v700.161a159.817 159.817 0 0 0 159.69 159.814h1278.97c21.01.06 41.83-4.02 61.26-12.01a159.956 159.956 0 0 0 51.97-34.557 160.186 160.186 0 0 0 34.78-51.828 160.06 160.06 0 0 0 12.27-61.193V162.09a160.006 160.006 0 0 0-12.12-61.35 159.746 159.746 0 0 0-34.74-51.995A159.676 159.676 0 0 0 4146.27 2.05Zm-992.5 554.559h-100.6c-2.24 0-4.38.89-5.98 2.476a8.492 8.492 0 0 0-2.48 5.983v143.234c0 2.243-.89 4.395-2.47 5.981a8.466 8.466 0 0 1-5.98 2.478h-70.84a8.596 8.596 0 0 1-5.95-2.511c-1.58-1.578-2.46-3.713-2.5-5.948V191.639c0-2.242.89-4.394 2.49-5.981a8.405 8.405 0 0 1 5.96-2.477h188.35c106.7 0 183.05 77.708 183.05 186.77 0 109.062-75.79 186.882-182.47 186.882l-.58-.224Zm530.09 151.581a8.16 8.16 0 0 1-.61 3.271 8.579 8.579 0 0 1-1.82 2.783 8.586 8.586 0 0 1-2.77 1.863 8.415 8.415 0 0 1-3.26.654h-66.88c-1.12 0-2.23-.223-3.26-.654a8.42 8.42 0 0 1-4.58-4.646 8.336 8.336 0 0 1-.61-3.271v-15.451a6.336 6.336 0 0 0-.95-3.865 6.215 6.215 0 0 0-3.06-2.54 6.273 6.273 0 0 0-3.96-.203 6.22 6.22 0 0 0-3.31 2.209c-19.97 21.767-52.45 37.556-104.22 37.556-76.23 0-126.66-39.699-126.66-108.272a100.528 100.528 0 0 1 10.05-48.408 100.413 100.413 0 0 1 31.92-37.758c27.51-20.64 70.14-31.354 133.41-33.836l67.1-2.255v-19.626c0-39.587-26.61-56.391-69.36-56.391-42.74 0-69.7 15.113-76 39.813a8.136 8.136 0 0 1-3.06 4.333 8.076 8.076 0 0 1-5.07 1.531h-66.2a8.38 8.38 0 0 1-3.55-.693 8.545 8.545 0 0 1-2.92-2.128 8.354 8.354 0 0 1-1.74-3.162 8.136 8.136 0 0 1-.25-3.603c9.93-58.648 58.42-103.198 161.85-103.198 109.85 0 149.43 51.092 149.43 148.649l.34 207.298Zm416.63-335.195-148.87 397.113c-33.84 92.256-93.06 115.942-157.91 115.942a125.984 125.984 0 0 1-36.2-4.398 8.757 8.757 0 0 1-4.63-3.089 8.823 8.823 0 0 1-1.8-5.258v-60.34c0-1.262.27-2.51.8-3.654a8.635 8.635 0 0 1 2.28-2.964 8.585 8.585 0 0 1 7.07-1.841 167.487 167.487 0 0 0 31.02 2.934 80.23 80.23 0 0 0 50.32-14.615 80.24 80.24 0 0 0 30.54-42.567l4.41-13.873a8.364 8.364 0 0 0 0-5.752l-139.19-357.524a8.629 8.629 0 0 1-.34-3.972c.21-1.333.72-2.6 1.48-3.698a8.731 8.731 0 0 1 3.01-2.625 8.564 8.564 0 0 1 3.86-.984h67.68c1.73.017 3.41.551 4.84 1.537a8.661 8.661 0 0 1 3.16 3.99l94.52 252.185a8.707 8.707 0 0 0 3.18 4.067 8.696 8.696 0 0 0 4.94 1.538c1.75 0 3.49-.535 4.93-1.538a8.583 8.583 0 0 0 3.18-4.067l82.01-251.508a8.376 8.376 0 0 1 3.09-4.264 8.41 8.41 0 0 1 5.01-1.601h69.38a8.628 8.628 0 0 1 3.86.904c1.19.6 2.24 1.472 3.04 2.547a8.549 8.549 0 0 1 1.56 3.643 8.422 8.422 0 0 1-.23 3.958v-.226Z"/>
                </svg>
                <div class="installment-payment-shoppay__message">
                  available at checkout
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
}